/**
 * Architects.js — B1: tracking architektów (LED key decision makers).
 *
 * Pokazuje architects_tracked collection (10 records dla Maskil).
 * W branży LED architekt/projektant oświetlenia jest KEY decydentem (industry_config).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users, MapPin, Briefcase, Calendar, Activity, BadgeCheck, ExternalLink,
  Search, RefreshCw, ChevronDown, ChevronUp, Sparkles, Database, Loader2, Info,
  TrendingUp, ArrowRight, Mail, Phone,
} from 'lucide-react';
import { listArchitects, getArchitectsStats, triggerGunbScan, enrichArchitectsContacts, getArchitectDetail } from '../lib/api';

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(String(s).split(' ')[0]);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s.slice(0, 10);
  }
}

function ArchitectCard({ arch, expanded, onToggle }) {
  const navigate = useNavigate();
  const projectsCount = (arch.projects || []).length;
  // Count unique linked investments
  const linkedInvestmentIds = new Set(
    (arch.projects || [])
      .filter(p => typeof p === 'object' && p.opportunity_id)
      .map(p => p.opportunity_id)
  );
  const linkedInvestmentsCount = linkedInvestmentIds.size;
  const isActive = arch.is_active;
  const lastActivity = arch.last_project_date
    ? new Date(arch.last_project_date).toLocaleDateString('pl-PL')
    : '—';

  return (
    <Card className={`glass-card lift-hover border-l-4 ${isActive ? 'border-l-emerald-500/70' : 'border-l-blue-500/50'}`}>
      <CardContent className="pt-3 pb-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Users className="w-4 h-4 text-primary shrink-0" />
              <span className="font-semibold text-sm">{arch.name || '—'}</span>
              {arch.license_number && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  <BadgeCheck className="w-3 h-3 mr-0.5" />
                  {arch.license_number}
                </Badge>
              )}
              {isActive ? (
                <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                  ✓ Aktywny
                </Badge>
              ) : isActive === false ? (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">
                  Nieaktywny
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">Status nieznany</Badge>
              )}
              {linkedInvestmentsCount > 0 && (
                <Badge className="text-[10px] bg-primary/15 text-primary border-primary/40 gap-1">
                  <TrendingUp className="w-2.5 h-2.5" />
                  {linkedInvestmentsCount} inwestycj{linkedInvestmentsCount === 1 ? 'a' : 'i'}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
              {arch.city && <span><MapPin className="w-3 h-3 inline mr-0.5" />{arch.city}</span>}
              {arch.specialization && <span><Briefcase className="w-3 h-3 inline mr-0.5" />{arch.specialization}</span>}
              <span><Calendar className="w-3 h-3 inline mr-0.5" />ostatni projekt: {lastActivity}</span>
              <span><Activity className="w-3 h-3 inline mr-0.5" />{projectsCount} projekt{projectsCount === 1 ? '' : 'ów'}</span>
            </div>

            {/* Quick summary: gdzie projektuje (top miasta) — kluczowa info dla handlowca */}
            {arch.top_project_cities?.length > 0 && (
              <div className="flex gap-1 mt-1.5 flex-wrap items-center">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">📍 Projektuje:</span>
                {arch.top_project_cities.slice(0, 5).map((c) => (
                  <Badge key={c.city} variant="outline" className="text-[10px] gap-0.5">
                    {c.city} ({c.count})
                  </Badge>
                ))}
                {arch.unique_cities_count > 5 && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    +{arch.unique_cities_count - 5}
                  </Badge>
                )}
              </div>
            )}

            {/* Top inwestorzy dla tego architekta */}
            {arch.top_investors?.length > 0 && (
              <div className="flex gap-1 mt-1 flex-wrap items-center">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">🏢 Dla inwestorów:</span>
                {arch.top_investors.slice(0, 3).map((inv) => (
                  <Badge key={inv.investor} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/20 gap-0.5">
                    {inv.investor.slice(0, 40)} ({inv.count})
                  </Badge>
                ))}
                {arch.unique_investors_count > 3 && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    +{arch.unique_investors_count - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" className="shrink-0 h-7 px-2">
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-3">
            {/* Projects */}
            {projectsCount > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Historia projektów (z GUNB pozwoleń)
                </div>
                <div className="space-y-2">
                  {arch.projects.slice(0, 5).map((p, i) => (
                    <div key={i} className="text-xs bg-muted/40 p-2 rounded">
                      {typeof p === 'object' ? (
                        <>
                          <div className="font-medium">{p.description?.slice(0, 200) || '—'}</div>
                          <div className="flex gap-3 mt-1 text-muted-foreground flex-wrap">
                            {p.city && <span>📍 {p.city}</span>}
                            {p.investor && <span>🏢 {p.investor}</span>}
                            {p.area_m2 ? <span>📐 {p.area_m2.toLocaleString()} m²</span> : null}
                            {p.date && <span>📅 {fmtDate(p.date)}</span>}
                            {p.permit_id && <span className="font-mono text-[10px]">📋 {p.permit_id}</span>}
                          </div>
                          {p.opportunity_id && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-[11px] text-primary mt-1 gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/investments/${encodeURIComponent(p.opportunity_id)}`);
                              }}
                            >
                              <TrendingUp className="w-2.5 h-2.5" />
                              Zobacz inwestycję w Pipeline B
                              <ArrowRight className="w-2.5 h-2.5" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <span>{String(p)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last known projects (web research) */}
            {arch.last_known_projects?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Aktywność z web research
                </div>
                <div className="space-y-1">
                  {arch.last_known_projects.slice(0, 3).map((p, i) => (
                    <div key={i} className="text-xs flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-[10px]">{p.type}</Badge>
                      <span>{p.name}</span>
                      {p.location && <span className="text-muted-foreground">· {p.location}</span>}
                      {p.estimated_completion && <span className="text-muted-foreground">· est. {p.estimated_completion}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
              <span>relacja: {arch.relationship_score || 0}/100</span>
              <span>discovered: {fmtDate(arch.discovered_at)}</span>
              <span>last check: {fmtDate(arch.last_activity_check)}</span>
              <span>source: {arch.source}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────
// ArchitectProfileView — deep-link view rendered when ?license= is in URL.
// Calls getArchitectDetail to get the full record (name, contacts, projects
// enriched with opportunity_id) and renders it above the list.
// ──────────────────────────────────────────────────────────
function ArchitectProfileView({ licenseNumber, onEnrichContacts, enriching }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    if (!licenseNumber) return;
    setProfileLoading(true);
    setProfileError(null);
    getArchitectDetail(licenseNumber)
      .then(d => setProfile(d))
      .catch(e => setProfileError(e.response?.data?.detail || e.message))
      .finally(() => setProfileLoading(false));
  }, [licenseNumber]);

  if (profileLoading) {
    return (
      <Card className="glass-card">
        <CardContent className="pt-4 pb-4 space-y-2">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (profileError) {
    return (
      <Card className="border-red-500/30 bg-red-500/5">
        <CardContent className="pt-4 pb-4 text-sm text-red-400">
          Nie udało się załadować profilu architekta: {profileError}
        </CardContent>
      </Card>
    );
  }

  if (!profile) return null;

  // Deduplicate projects: prefer opportunity_id, fall back to permit_id, then description.
  const seenKeys = new Set();
  const dedupedProjects = (profile.projects || []).filter(p => {
    if (typeof p !== 'object') return true;
    const key = p.opportunity_id || p.permit_id || p.description;
    if (!key) return true;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const hasContacts = !!(profile.email || profile.phone || profile.website || profile.studio_name);

  return (
    <Card className="glass-card border-l-4 border-l-primary/60">
      <CardContent className="pt-4 pb-4 space-y-4">
        {/* Header: name + identity badges */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Users className="w-5 h-5 text-primary shrink-0" />
            <span className="text-lg font-bold">{profile.name || '—'}</span>
            {profile.license_number && (
              <Badge variant="outline" className="text-[11px] font-mono">
                <BadgeCheck className="w-3 h-3 mr-0.5" />
                {profile.license_number}
              </Badge>
            )}
            {profile.is_active ? (
              <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                ✓ Aktywny
              </Badge>
            ) : profile.is_active === false ? (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                Nieaktywny
              </Badge>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            {profile.city && <span><MapPin className="w-3 h-3 inline mr-0.5" />{profile.city}</span>}
            {profile.voivodeship && <span>{profile.voivodeship}</span>}
            {profile.specialization && <span><Briefcase className="w-3 h-3 inline mr-0.5" />{profile.specialization}</span>}
          </div>
        </div>

        {/* Contact block — shown only when contacts exist; honest note otherwise */}
        {hasContacts ? (
          <div className="bg-muted/30 rounded-md p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
              Dane kontaktowe
            </div>
            {profile.studio_name && (
              <div className="text-sm font-medium">{profile.studio_name}</div>
            )}
            {profile.email && (
              <a href={`mailto:${profile.email}`}
                 className="text-xs text-primary hover:underline flex items-center gap-1">
                <Mail className="w-3 h-3" /> {profile.email}
              </a>
            )}
            {profile.phone && (
              <a href={`tel:${profile.phone}`}
                 className="text-xs text-primary hover:underline flex items-center gap-1">
                <Phone className="w-3 h-3" /> {profile.phone}
              </a>
            )}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer"
                 className="text-xs text-primary hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> {profile.website}
              </a>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/20 rounded-md p-3">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
            <span>
              Kontakt nieznany — rejestr GUNB zawiera tylko nazwisko i numer uprawnień.
            </span>
            {onEnrichContacts && (
              <Button onClick={onEnrichContacts} disabled={enriching} variant="outline" size="sm"
                      className="ml-auto shrink-0 h-7 text-[11px] gap-1">
                {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                Znajdź kontakty
              </Button>
            )}
          </div>
        )}

        {/* Projects list (deduplicated) */}
        {dedupedProjects.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Historia projektów ({dedupedProjects.length})
            </div>
            <div className="space-y-2">
              {dedupedProjects.map((p, i) => (
                <div key={i} className="text-xs bg-muted/40 p-2 rounded">
                  {typeof p === 'object' ? (
                    <>
                      <div className="font-medium">{p.description?.slice(0, 200) || '—'}</div>
                      <div className="flex gap-3 mt-1 text-muted-foreground flex-wrap">
                        {p.city && <span>📍 {p.city}</span>}
                        {p.investor && <span>🏢 {p.investor}</span>}
                        {p.area_m2 ? <span>📐 {p.area_m2.toLocaleString()} m²</span> : null}
                        {p.date && <span>📅 {fmtDate(p.date)}</span>}
                        {p.permit_id && <span className="font-mono text-[10px]">📋 {p.permit_id}</span>}
                      </div>
                      {p.opportunity_id && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-[11px] text-primary mt-1 gap-1"
                          onClick={() => navigate(`/investments/${encodeURIComponent(p.opportunity_id)}`)}
                        >
                          <TrendingUp className="w-2.5 h-2.5" />
                          Zobacz inwestycję w Pipeline B
                          <ArrowRight className="w-2.5 h-2.5" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <span>{String(p)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Architects() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [enriching, setEnriching] = useState(false);

  async function handleEnrichContacts() {
    setEnriching(true);
    const tId = toast.loading('Szukam kontaktów (priorytet: architekci z największą liczbą inwestycji)...');
    try {
      const r = await enrichArchitectsContacts(20);
      if (r.found_email === 0 && r.found_phone === 0) {
        toast.warning(
          `Przeskanowano ${r.scanned}, ale 0 kontaktów. Sprawdź czy Serper ma kredyty (enrichment używa wyszukiwarki).`,
          { id: tId, duration: 8000 }
        );
      } else {
        toast.success(
          `Znaleziono: ${r.found_email} email, ${r.found_phone} telefon. Zaktualizowano ${r.chains_updated} łańcuchów.`,
          { id: tId, duration: 7000 }
        );
      }
      await load();
    } catch (e) {
      toast.error('Enrichment nie powiódł się: ' + (e.response?.data?.detail || e.message), { id: tId });
    } finally {
      setEnriching(false);
    }
  }
  const [expanded, setExpanded] = useState({});

  // T31 2026-05-29 — handle ?license=X query param deep-link
  // Bug zgłoszenia 76dbdc93: klik 'Profil' w decision chain wraca do listy
  // bo Architects.js nie obsługuje query param. Naprawiamy:
  const _initialLicense = (() => {
    if (typeof window === 'undefined') return undefined;
    try { return new URLSearchParams(window.location.search).get('license') || undefined; }
    catch { return undefined; }
  })();

  // Default: search by 'project_city' (gdzie architekt projektuje, niezależnie od biura)
  // — bo to częściej szukane przez handlowca ("kogo skontaktować w Elblągu")
  // 2026-05-27 fix #1: architect_type = 'architect' | 'interior_designer' | 'all'
  const [filters, setFilters] = useState({
    sort: 'last_project',
    city_mode: 'project',
    architect_type: _initialLicense ? 'all' : 'architect',  // gdy deep-link → all żeby na pewno znaleźć
    q: _initialLicense,  // search po license_number jako q
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.is_active !== undefined && { is_active: filters.is_active }),
        ...(filters.specialization && { specialization: filters.specialization }),
        ...(filters.investor && { investor: filters.investor }),
        ...(filters.q && { q: filters.q }),
        sort: filters.sort,
      };
      // 2026-05-27 fix #1: architect_type filter
      if (filters.architect_type && filters.architect_type !== 'all') {
        params.architect_type = filters.architect_type;
      }
      // Geografia: 'office' = biuro architekta (pole `city`), 'project' = gdzie projektuje (projects[].city)
      if (filters.city) {
        if (filters.city_mode === 'office') {
          params.city = filters.city;
        } else {
          params.project_city = filters.city;
        }
      }
      const r = await listArchitects(params);
      setData(r);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const s = await getArchitectsStats();
      setStats(s);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { loadStats(); }, []);

  // T31 — auto-expand pierwszej karty gdy deep-link z ?license=X
  useEffect(() => {
    if (_initialLicense && data?.items?.length > 0) {
      // Klucz MUSI być taki sam jak w renderze karty (license_number || name),
      // inaczej auto-expand nie trafia i user "wraca do listy" (zgł. 76dbdc93).
      const firstKey = data.items[0].license_number || data.items[0].name;
      if (firstKey && !expanded[firstKey]) {
        setExpanded({ [firstKey]: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function handleScanGunb(voivodeship = null) {
    setScanning(true);
    try {
      const label = voivodeship || 'wszystkich 16 województw';
      toast.info(`Skanuję GUNB dla ${label} — może to potrwać kilka minut...`);
      const r = await triggerGunbScan(voivodeship, 30);
      const newCount = r.architects_new || 0;
      const updatedCount = r.architects_updated || 0;
      toast.success(
        `✓ Skan zakończony: ${newCount} nowych architektów, ${updatedCount} zaktualizowanych. Łącznie ${r.total_permits} pozwoleń przetworzonych.`,
        { duration: 8000 }
      );
      await load();
      await loadStats();
    } catch (e) {
      toast.error('Błąd skanu: ' + (e.response?.data?.detail || e.message));
    } finally {
      setScanning(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Architekci — kluczowi decydenci
          </h1>
          <p className="text-sm text-muted-foreground">
            Architekci śledzeni z pozwoleń GUNB. Kluczowy decydent inwestycji — wpływa na wybór dostawców i rozwiązań.
            {data?.stats && (
              <span className="ml-2">
                <Badge variant="outline" className="ml-1">{data.stats.total_tracked} tracked</Badge>
                <Badge className="ml-1 bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{data.stats.active} aktywnych</Badge>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleEnrichContacts} disabled={enriching} variant="outline" size="sm"
                  title="Znajdź email/telefon architektów — priorytet: najwięcej inwestycji">
            {enriching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
            {enriching ? 'Szukam kontaktów...' : 'Znajdź kontakty'}
          </Button>
          <Button onClick={load} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Odśwież
          </Button>
        </div>
      </div>

      {/* 2026-05-27 fix #1 — tab switcher: Architekci budynków vs Projektanci wnętrz */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setFilters((f) => ({ ...f, architect_type: 'architect' }))}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
            filters.architect_type === 'architect'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-architects-buildings"
        >
          🏢 Architekci budynków
          {filters.architect_type === 'architect' && data?.stats && (
            <Badge variant="outline" className="ml-2 text-[10px]">{data.stats.total_tracked || data.items?.length || 0}</Badge>
          )}
        </button>
        <button
          onClick={() => setFilters((f) => ({ ...f, architect_type: 'interior_designer' }))}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
            filters.architect_type === 'interior_designer'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-architects-interior"
        >
          🎨 Projektanci wnętrz
          {filters.architect_type === 'interior_designer' && data?.stats && (
            <Badge variant="outline" className="ml-2 text-[10px]">{data.stats.total_tracked || data.items?.length || 0}</Badge>
          )}
        </button>
        <button
          onClick={() => setFilters((f) => ({ ...f, architect_type: 'all' }))}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
            filters.architect_type === 'all'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Wszyscy
        </button>
      </div>

      {filters.architect_type === 'interior_designer' && (data?.items || []).length === 0 && !loading && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-4 text-sm">
            <p className="font-medium mb-1">📂 Baza projektantów wnętrz na razie pusta</p>
            <p className="text-muted-foreground">
              Architekci z GUNB to projektanci budynków (mają pozwolenia). Projektanci wnętrz pracują nad
              istniejącymi przestrzeniami i nie pojawiają się w GUNB. Import z SARP / SAW / Behance / LinkedIn
              planowany w kolejnej iteracji. Możesz na razie dodać ich ręcznie z poziomu kontrahentów.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Deep-link profile view — rendered when ?license= is in URL (bug report a) */}
      {_initialLicense && (
        <ArchitectProfileView
          licenseNumber={_initialLicense}
          onEnrichContacts={handleEnrichContacts}
          enriching={enriching}
        />
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-3 pb-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-end">
            {/* T34 2026-05-29 — name search (Miro request — brakowało) */}
            <div className="flex-1 min-w-[260px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Imię / nazwisko / numer uprawnień:
              </label>
              <Input
                placeholder="np. Kowalski, Anna Nowak, WP-OIA/OKK/123/2020"
                value={filters.q || ''}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value || undefined }))}
              />
            </div>

            {/* GEOGRAPHIC FILTER z toggle "biuro vs projekt" */}
            <div className="flex-1 min-w-[280px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Lokalizacja:
                <Select
                  value={filters.city_mode || 'project'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, city_mode: v }))}
                >
                  <SelectTrigger className="inline-flex h-5 ml-1 text-[10px] border-0 px-1 py-0 bg-transparent w-auto gap-0.5 text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">📍 gdzie projektuje (cross-region)</SelectItem>
                    <SelectItem value="office">🏢 biuro / baza architekta</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <Input
                placeholder={filters.city_mode === 'office'
                  ? 'np. Gdańsk (znajdzie tylko architektów z biurem w Gdańsku)'
                  : 'np. Elbląg (znajdzie WSZYSTKICH którzy tam projektują — niezależnie od biura)'}
                value={filters.city || ''}
                onChange={e => setFilters(f => ({ ...f, city: e.target.value || undefined }))}
              />
            </div>

            {/* INWESTOR FILTER */}
            <div className="min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Inwestor (projektował dla...):
              </label>
              <Input
                placeholder="np. Echo Investment"
                value={filters.investor || ''}
                onChange={e => setFilters(f => ({ ...f, investor: e.target.value || undefined }))}
              />
            </div>

            <div className="min-w-[160px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Specjalizacja:
              </label>
              <Input
                placeholder="np. biurowe"
                value={filters.specialization || ''}
                onChange={e => setFilters(f => ({ ...f, specialization: e.target.value || undefined }))}
              />
            </div>

            <Select
              value={filters.is_active === undefined ? 'all' : (filters.is_active ? 'active' : 'inactive')}
              onValueChange={v => setFilters(f => ({ ...f, is_active: v === 'all' ? undefined : v === 'active' }))}
            >
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszyscy</SelectItem>
                <SelectItem value="active">Aktywni</SelectItem>
                <SelectItem value="inactive">Nieaktywni</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.sort} onValueChange={v => setFilters(f => ({ ...f, sort: v }))}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="region">🗺️ Regionami</SelectItem>
                <SelectItem value="specialization">🏷️ Branżami</SelectItem>
                <SelectItem value="last_project">Ostatni projekt</SelectItem>
                <SelectItem value="projects_count">Liczba projektów</SelectItem>
                <SelectItem value="name">Nazwa</SelectItem>
                <SelectItem value="city">Miasto biura</SelectItem>
                <SelectItem value="relationship">Relacja</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Hint */}
          {filters.city_mode === 'project' && filters.city && (
            <div className="text-[11px] text-primary/80 italic">
              💡 Szukasz architektów którzy mają projekt w mieście „{filters.city}" — nieważne czy pracują z Warszawy, Gdańska czy Zakopanego.
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : !data || data.total === 0 ? (
        <Card>
          <CardContent className="pt-6 pb-6">
            <div className="text-center">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50 text-muted-foreground" />
              <p className="text-sm font-medium">
                {filters.city
                  ? `Brak architektów w mieście "${filters.city}"`
                  : 'Brak architektów spełniających kryteria'}
              </p>

              {/* Contextual info: ile mamy w bazie ogółem + top miasta */}
              {stats && stats.total > 0 && (
                <div className="mt-4 max-w-md mx-auto space-y-3 text-left">
                  <div className="text-xs text-muted-foreground flex items-center gap-2 justify-center">
                    <Database className="w-3.5 h-3.5" />
                    W bazie masz <strong className="text-foreground">{stats.total}</strong> architektów
                    ({stats.active} aktywnych)
                  </div>

                  {stats.by_city.length > 0 && (
                    <div className="bg-muted/30 rounded-md p-3 space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                        Miasta w których mamy architektów:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {stats.by_city.slice(0, 10).map((c) => (
                          <Badge
                            key={c.city}
                            variant="outline"
                            className="cursor-pointer hover:bg-primary/10 text-[11px]"
                            onClick={() => setFilters((f) => ({ ...f, city: c.city }))}
                          >
                            <MapPin className="w-2.5 h-2.5 mr-0.5" />
                            {c.city} ({c.count})
                          </Badge>
                        ))}
                      </div>
                      {filters.city && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => setFilters((f) => ({ ...f, city: undefined }))}
                          className="text-xs h-auto p-0"
                        >
                          ✕ Wyczyść filtr "{filters.city}"
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Info o GUNB scanner + manual trigger */}
              <div className="mt-4 max-w-md mx-auto space-y-2">
                <div className="text-xs text-muted-foreground flex items-start gap-2 text-left bg-blue-500/5 border border-blue-500/20 rounded-md p-3">
                  <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <div>
                    Architekci są pobierani z <strong>pozwoleń budowlanych GUNB</strong>.
                    Cron skanuje 16 województw raz w tygodniu (cache 24h per region).
                    {stats?.next_gunb_scan?.active === false && (
                      <div className="mt-1 text-amber-400">⚠ Cron jest wyłączony — uruchom manualnie.</div>
                    )}
                  </div>
                </div>

                {filters.city ? (
                  <Button
                    onClick={() => handleScanGunb(null)}
                    disabled={scanning}
                    className="gap-1.5"
                    variant="default"
                  >
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Skanuj wszystkie województwa GUNB
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleScanGunb(null)}
                    disabled={scanning}
                    className="gap-1.5"
                    variant="default"
                  >
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Uruchom pełen skan GUNB (16 województw, ~5-10 min)
                  </Button>
                )}
                {scanning && (
                  <p className="text-[11px] text-muted-foreground italic">
                    Skan w toku — pobieranie CSV per województwo + parsing + zapis. Możesz zostawić stronę otwartą.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map(arch => (
            <ArchitectCard
              key={arch.license_number || arch.name}
              arch={arch}
              expanded={!!expanded[arch.license_number || arch.name]}
              onToggle={() => setExpanded(e => ({
                ...e,
                [arch.license_number || arch.name]: !e[arch.license_number || arch.name],
              }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
